import { useState } from "react";
import { Phone } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateProfile, getGetMeQueryKey } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const DISMISS_KEY = "ayrshire_phone_modal_dismissed";

function isDismissed() {
  try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
}
function persistDismiss() {
  try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
}

export function PhoneRequiredModal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState("");
  const [dismissed, setDismissed] = useState(() => isDismissed());
  const [error, setError] = useState("");
  const { mutateAsync: updateProfile, isPending } = useUpdateProfile();

  // Don't show for guests or users who already have a phone
  const show = !!user && (user as { role?: string }).role !== "guest" && !user.phone && !dismissed;

  function dismiss() {
    persistDismiss();
    setDismissed(true);
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) {
      setError("Please enter your mobile number.");
      return;
    }
    setError("");
    try {
      await updateProfile({ data: { phone: trimmed } });
      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      persistDismiss();
    } catch {
      setError("Couldn't save your number — please try again.");
    }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-2">
            <Phone className="text-primary" size={24} />
          </div>
          <DialogTitle className="text-lg">Add your mobile number</DialogTitle>
          <DialogDescription>
            We need your phone number so we can contact you about jobs, leads, and activity on Ayrshire Hub.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="mt-1 flex flex-col gap-3">
          <div className={`flex items-center gap-3 border rounded-xl px-4 py-3 bg-slate-50 ${error ? "border-red-400" : "border-slate-200"}`}>
            <Phone size={15} className="text-slate-400 flex-shrink-0" />
            <input
              type="tel"
              autoComplete="tel"
              placeholder="e.g. 07700 900000"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setError(""); }}
              className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 bg-transparent outline-none"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={isPending}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-xl transition-opacity disabled:opacity-70"
          >
            {isPending
              ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : "Save number"
            }
          </button>

          <button
            type="button"
            onClick={dismiss}
            className="text-sm text-slate-400 hover:text-slate-600 text-center py-1"
          >
            Remind me later
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
